import type { RuntimeSnapshot } from '../app/runtime-types.ts';

interface VisitorLaunchPanelProps {
  readonly kind: 'public' | 'unlisted';
  readonly title: string;
  readonly creatorName: string;
  readonly description: string;
  readonly allowRemix: boolean;
  readonly snapshot: RuntimeSnapshot;
  readonly busy: boolean;
  readonly message: string | null;
  readonly onRide: () => void;
  readonly onRideWithReaction: () => void;
  readonly onRemix: () => Promise<void>;
  readonly onExit: () => void;
}

export function VisitorLaunchPanel(props: VisitorLaunchPanelProps) {
  const intensity = props.snapshot.fullValidation.intensity;
  const technicalReady = props.snapshot.fullValidation.technicalEligible;
  return (
    <section
      class="visitor-launch"
      aria-labelledby="visitor-coaster-title"
      data-testid="visitor-launch"
    >
      <span class="eyebrow">
        {props.kind === 'public' ? 'Buncha coaster' : 'Private read-only ride'}
      </span>
      <h2 id="visitor-coaster-title">{props.title}</h2>
      <p class="visitor-creator">Built by {props.creatorName}</p>
      {props.description && <p class="visitor-description">{props.description}</p>}

      <div class={`visitor-intensity intensity-${intensity?.level ?? 'unknown'}`}>
        <span>Simulated intensity</span>
        <strong>{intensity?.label ?? 'Checking ride…'}</strong>
        {intensity && <small>{intensity.message}</small>}
      </div>

      <dl class="visitor-metrics">
        <div>
          <dt>Top speed</dt>
          <dd>
            {props.snapshot.fullValidation.topSpeedMph === null
              ? '—'
              : `${props.snapshot.fullValidation.topSpeedMph.toFixed(1)} mph`}
          </dd>
        </div>
        <div>
          <dt>Positive G</dt>
          <dd>
            {props.snapshot.fullValidation.maxVerticalG === null
              ? '—'
              : `${props.snapshot.fullValidation.maxVerticalG.toFixed(2)} G`}
          </dd>
        </div>
        <div>
          <dt>Lateral G</dt>
          <dd>
            {props.snapshot.fullValidation.maxAbsLateralG === null
              ? '—'
              : `${props.snapshot.fullValidation.maxAbsLateralG.toFixed(2)} G`}
          </dd>
        </div>
      </dl>

      {!technicalReady && !props.snapshot.fullValidation.pending && (
        <p class="visitor-unavailable">This revision is not currently rideable.</p>
      )}
      {props.message && (
        <p class="visitor-message" role="status">
          {props.message}
        </p>
      )}
      <div class="visitor-actions">
        <button
          type="button"
          class="primary-action"
          disabled={!technicalReady || props.busy}
          onClick={props.onRide}
          data-testid="ride-public-coaster"
        >
          Ride coaster
        </button>
        {props.snapshot.reaction.eligibility.state === 'available' && (
          <button
            type="button"
            class="secondary-action"
            disabled={!technicalReady || props.busy}
            onClick={props.onRideWithReaction}
          >
            Ride with Reaction Cam
          </button>
        )}
        {props.allowRemix && (
          <button
            type="button"
            class="secondary-action"
            disabled={props.busy}
            onClick={() => void props.onRemix()}
          >
            Remix as a new draft
          </button>
        )}
        <button type="button" class="visitor-exit" onClick={props.onExit}>
          Exit
        </button>
      </div>
      <small class="visitor-privacy">
        Viewing this coaster never grants edit access to the creator’s project.
      </small>
    </section>
  );
}
